import axios from 'axios';
import {
	get,
} from 'lodash';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';

function fireClickyGoal({ clicky_goal_id }) {
	try {
		if (clicky_goal_id) {
			console.info('Firing clicky goal',
				clicky_goal_id);
			clicky.goal(clicky_goal_id);
		}
	} catch(err) {
		console.error(`Failed to send clicky goal: ${clicky_goal_id}`);
	}
}

function fireClickyGoalByName(name, amount) {
	try {
		if (name) {
			console.info('Firing clicky goal',
				name);
			clicky.goal(name,
				amount);
		}
	} catch(err) {
		console.error(`Failed to send clicky goal: ${name}`);
	}
}

function fireGoogleEcommerce({
	amount, charge_id, purchased_items, prepurchased_med,
}, products, patient) {
	// Fires both the Google E-Commerce transaction and
	// Google Analytics Event (used for Goal tracking)
	try {
		if (charge_id) {
			// Setup for firing Google Analytics Event
			let event_category = '',
				event_action = '',
				event_label = '',
				event_value = parseInt(amount) || 0, // this amount appears to be the original total (does not include discounts)
				is_new_patient = !!(patient && ['PAYMENT', 'MEDICATION_SELECTION'].includes(patient.registrationStep)),
				is_cross_sell = !!(!is_new_patient && patient && patient.registrationStep === 'CROSS_SELL'); // fake step, for cross-sell purchases immediately after initial payment

			// Google E-Commerce
			ga('ecommerce:addTransaction',
				{
					id: charge_id,
					affiliation: 'Apostrophe',
					revenue: String(amount),
					shipping: '0',
					tax: '0',
				});
			if (!isEmpty(purchased_items)) {
				// Single purchase medication(s) [SKIN]
				// Provided via response from server since products is missing product names
				forEach(purchased_items,
					({
						id, rx_type, purchased_option,
					}, key) => {
						ga('ecommerce:addItem',
							{
								id: [String(charge_id), String(id)].join('_'), // Transaction ID. Required.
								name: rx_type, // Product name. Required.
								price: String(purchased_option.price_cents / 100), // Unit price.
								quantity: '1', // Quantity.
								category: 'SKIN', // Category or variation.
							});
					});
				event_category = 'SKIN';
				event_label = 'SINGLE';
				if (is_new_patient) {
					event_action = 'REG_PAYMENT'
				} else {
					event_action = 'RET_PAYMENT';
				}
			} else if (!isEmpty(products)) {
				// Subscription/single purchase products [LASH/HAIR/FILI] passed in via props
				forEach(products,
					({
						frequency, product_type,
					}, key) => {
						let newItem = {
							id: charge_id, // Transaction ID. Required.
							name: product_type, // Product name. Required.
							price: String(amount), // Unit price.
							quantity: '1',
							category: product_type, // Category or variation.
						};
						if (frequency == 0){
							newItem.category = 'SinglePurchase';
							event_label = 'SINGLE';
						} else {
							newItem.category = 'Subscription';
							event_label = 'SUBSCRIPTION'; // default
							if (frequency == 1) {
								event_label = 'MONTHLY';
							} else if (frequency == 2) {
								event_label = 'BIMONTHLY';
							} else if (frequency == 3) {
								event_label = 'QUARTERLY';
							}
						}
						ga('ecommerce:addItem',
							newItem);
						event_category = product_type;
						if (is_new_patient) {
							event_action = 'REG_PAYMENT'
						} else if (is_cross_sell) {
							event_action = 'CROSS_SELL_PAYMENT'
						} else {
						// NOTE: React Checkout does not currently handle processing payments for this case
						// #TODO detect first time purchase of this product
							event_action = 'RET_PAYMENT';
						}
					});
			} else if (prepurchased_med) {
				// Pre-purchase meds flow visit fee checkout
				let newItem = {
					id: charge_id, // Transaction ID. Required.
					name: 'Prepurchase ' + String(prepurchased_med), // "Prepurchase Doxycycline" (AB Test prepurchase med name)
					price: String(amount), // Unit price [dollars]
					quantity: '1',
					category: 'Consultation',
				};
				ga('ecommerce:addItem',
					newItem);
				event_category = 'VISIT';
				event_action = 'REG_PAYMENT';
			} else {
				// Doctor visit fee checkout
				let newItem = {
					id: charge_id, // Transaction ID. Required.
					name: 'Doctor Visit fee', // Product name. Required.
					price: String(amount), // Unit price.
					quantity: '1',
					category: 'Consultation',
				};
				ga('ecommerce:addItem',
					newItem);
				event_category = 'VISIT';
				if (is_new_patient) {
					event_action = 'REG_PAYMENT';
				} else {
					event_action = 'FOL_PAYMENT';
				}
			}
			ga('ecommerce:send');

			// Google Analytics Event
			// Map legacy product types to 4-letter business line designations
			const PRODUCT_TO_BUSINESS_LINE = {
				LATISSE: 'LASH',
				MPB: 'HAIR',
				WRINKLE: 'FILI',
			};
			if (event_category && PRODUCT_TO_BUSINESS_LINE[event_category]) {
				event_category = PRODUCT_TO_BUSINESS_LINE[event_category];
			}
			let ga_event = {
				hitType: 'event',
				eventCategory: event_category, // Typically the object that was interacted with
				eventAction: event_action, // The type of interaction
				eventValue: event_value, // <integer> A numeric value associated with the event
			};
			if (event_label) {
				// [optional] Useful for categorizing events
				ga_event.eventLabel = event_label;
			}
			ga('send',
				ga_event);

			// For power digital tracking
			if (typeof gtag !== 'undefined' && gtag) {
				gtag('event',
					'conversion',
					{
						send_to: 'AW-979776132/kx9tCLDb2aYCEITlmNMD',
						transaction_id: charge_id,
					});
			}
		}
	} catch(err) {
		console.error('Error in GA E-commerce event');
		console.error(err)
	}
}

export const createABTest = async ({
	csrfToken,
	patientID,
	consultationID,
	campaignName,
	group,
	rx_type,
	initGoal,
}) => {
	try {
		if (!csrfToken || !patientID || !consultationID || !campaignName) {
			throw new Error('Missing required param');
		}
		const headers = {
			credentials: 'include',
			'X-CSRFToken': csrfToken,
		};

		const res = await axios({
			url: '/ajax/ab_test/create/',
			method: 'POST',
			headers,
			data: {
				group,
				rx_type,
				patient_id: patientID,
				consultation_id: consultationID,
				init_goal: initGoal,
				campaign_name: campaignName,
			},
		});

		if (!res || !res.data) throw new Error('Missing res');
		const { error } = res.data;
		if (error) throw new Error(error);
		// Handle for cases when the ab test and campaign already exist
		if (res.data.success !== '') {
			return { success: true };
		} else {
			return {
				success: true,
				exists: true,
			};
		}
	} catch (error) {
		console.error(error);
		return { error };
	}
}

export const fireABTestGoal = async ({
	csrfToken, campaignName, goal, patientId, consultationId, goalOrOther, value,
}) => {
	try {
		if (!csrfToken || !campaignName || !goal || !patientId || !consultationId) {
			throw new Error('Missing required param');
		}
		const headers = {
			credentials: 'include',
			'X-CSRFToken': csrfToken,
		}
		const res = await axios({
			url: '/ajax/ab_test/log_goal/',
			method: 'PUT',
			headers,
			data: {
				campaign_name: campaignName,
				goal,
				patient_id: patientId,
				consultation_id: consultationId,
				// Allows you to set the "goal" to another object within the ab test i.e. answers
				goalOrOther,
				value,
			},
		});

		if (!res || !res.data) throw new Error('Missing res');
		const { error } = res.data;
		if (error) throw new Error(error);
		return { success: 'Success' }
	} catch (error) {
		console.error(error);
		return { error };
	}
}

export {
	fireClickyGoal, fireClickyGoalByName,fireGoogleEcommerce,
}

export async function handleModalCreateABTest(product, handleModalOpen, patientID, consultationID, clientToken) {
	// noModal true if they have already recieved,
	const noModal = get(window,
		'Yoderm.data.noModal',
		'');
	const noTopicalSurvey = get(window,
		'Yoderm.data.noTopicalSurvey',
		'');
	if (noModal === 'false') {
		let group = 'topical_survey';
		// Topical Survey must have a topical removed, and must be available to use
		if (product.medication_type !== 'topical' || noTopicalSurvey === 'true') {
			return;
		}
		// Create the AB test for topical survey (if eligible)
		const campaignName = 'topical_removed_from_cart';
		const rx_type = get(product,
			'rx_type',
			'');
		const abTestCreated = await createABTest({
			csrfToken: clientToken,
			patientID,
			consultationID,
			campaignName,
			group,
			rx_type,
			initGoal: 'item_removed',
		});
		if (abTestCreated.success && !abTestCreated.exists) {
			handleModalOpen(product,
				false);
			return true;
		}
	}
	return false;
}
